import { DocField } from '../../core/types';

/**
 * Parser for ICAO 9303 TD1 machine-readable zones (ID-1 cards, e.g. the
 * Brazilian CIN). TD1 is three lines of 30 characters, but some cards print
 * only the two data lines; the optional name line is handled when present.
 */
export interface MrzResult {
  documentType: string;
  issuingCountry: string;
  documentNumber: string;
  documentNumberValid: boolean;
  optionalData1: string;
  birthDate: string;
  birthDateValid: boolean;
  sex: string;
  expiryDate: string;
  expiryDateValid: boolean;
  nationality: string;
  optionalData2: string;
  compositeValid: boolean;
  surname?: string;
  givenNames?: string;
}

function charValue(c: string): number {
  if (c === '<') return 0;
  if (c >= '0' && c <= '9') return c.charCodeAt(0) - 48;
  if (c >= 'A' && c <= 'Z') return c.charCodeAt(0) - 55;
  return 0;
}

/** ICAO check digit over a field using the 7-3-1 weighting. */
function checkDigit(field: string): number {
  const weights = [7, 3, 1];
  let sum = 0;
  for (let i = 0; i < field.length; i++) {
    sum += charValue(field[i]) * weights[i % 3];
  }
  return sum % 10;
}

function digitMatches(field: string, expected: string): boolean {
  return expected >= '0' && expected <= '9' && checkDigit(field) === Number(expected);
}

/** YYMMDD → DD/MM/YYYY, inferring century (>= 30 ⇒ 1900s, else 2000s). */
function formatDate(yymmdd: string): string {
  if (!/^\d{6}$/.test(yymmdd)) return yymmdd;
  const yy = Number(yymmdd.slice(0, 2));
  const century = yy >= 30 ? 1900 : 2000;
  return `${yymmdd.slice(4, 6)}/${yymmdd.slice(2, 4)}/${century + yy}`;
}

function cleanFiller(value: string): string {
  return value.replace(/</g, ' ').trim().replace(/\s+/g, ' ');
}

/**
 * Parses TD1 MRZ text. Accepts the lines separated by newlines or run together;
 * tolerates spaces/lowercase. Returns null if it doesn't look like TD1.
 */
export function parseTd1(input: string): MrzResult | null {
  const normalized = input.toUpperCase().replace(/[^A-Z0-9<\n]/g, '');
  let lines = normalized.split('\n').map((l) => l.trim()).filter(Boolean);

  // If pasted as one run of characters, split into 30-char lines.
  if (lines.length === 1 && lines[0].length >= 60) {
    lines = lines[0].match(/.{1,30}/g) ?? [];
  }
  if (lines.length < 2) return null;

  const [l1, l2, l3] = lines;
  if (l1.length < 30 || l2.length < 30) return null;

  const documentNumber = l1.slice(5, 14);
  const documentNumberCD = l1[14];
  const birthDate = l2.slice(0, 6);
  const birthDateCD = l2[6];
  const expiryDate = l2.slice(8, 14);
  const expiryDateCD = l2[14];

  // Composite check covers specific spans of lines 1 and 2 (ICAO 9303 TD1).
  const compositeInput = l1.slice(5, 30) + l2.slice(0, 7) + l2.slice(8, 15) + l2.slice(18, 29);

  const result: MrzResult = {
    documentType: cleanFiller(l1.slice(0, 2)),
    issuingCountry: l1.slice(2, 5).replace(/</g, ''),
    documentNumber: documentNumber.replace(/</g, ''),
    documentNumberValid: digitMatches(documentNumber, documentNumberCD),
    optionalData1: cleanFiller(l1.slice(15, 30)),
    birthDate: formatDate(birthDate),
    birthDateValid: digitMatches(birthDate, birthDateCD),
    sex: l2[7] === 'M' ? 'M' : l2[7] === 'F' ? 'F' : cleanFiller(l2[7]),
    expiryDate: formatDate(expiryDate),
    expiryDateValid: digitMatches(expiryDate, expiryDateCD),
    nationality: l2.slice(15, 18).replace(/</g, ''),
    optionalData2: cleanFiller(l2.slice(18, 29)),
    compositeValid: digitMatches(compositeInput, l2[29]),
  };

  if (l3) {
    const [surname, given] = l3.split('<<');
    result.surname = cleanFiller(surname ?? '');
    result.givenNames = cleanFiller(given ?? '');
  }
  return result;
}

const ok = (valid: boolean) => (valid ? '✓' : '✗ check failed');

/** Maps an MRZ result to the uniform labelled-field list. */
export function mrzToFields(m: MrzResult): DocField[] {
  const fields: DocField[] = [
    { label: 'Document type', value: m.documentType },
    { label: 'Issuing country', value: m.issuingCountry },
    { label: 'Document number', value: `${m.documentNumber} (${ok(m.documentNumberValid)})` },
    { label: 'Birth date', value: `${m.birthDate} (${ok(m.birthDateValid)})` },
    { label: 'Sex', value: m.sex },
    { label: 'Expiry date', value: `${m.expiryDate} (${ok(m.expiryDateValid)})` },
    { label: 'Nationality', value: m.nationality },
  ];
  if (m.optionalData1) fields.push({ label: 'Optional data', value: m.optionalData1 });
  if (m.surname || m.givenNames) {
    fields.push({ label: 'Name', value: `${m.givenNames ?? ''} ${m.surname ?? ''}`.trim() });
  }
  fields.push({ label: 'Composite check', value: ok(m.compositeValid) });
  return fields;
}
